const FULFILLMENT_COOKIE = "selected_fulfillment";
const FULFILLMENT_COOKIE_MAX_AGE = 7 * 24 * 60 * 60; // 7 days

const fulfillmentListeners = {
    cardClickListeners: [],
    cardKeydownListeners: [],
    addToCartListener: null,
    storeSelectListener: null,
};

async function getVariantInventoryLocations(variantId) {
    const params = new URLSearchParams({
        variant_id: variantId,
    });

    const response = await fetch(`/apps/locations/shopify/inventory-locations?${params.toString()}`,
        {
            method: "GET",
            headers: {
                Accept: "application/json",
                "x-api-key": "JSq1QBUfQWejPnhpJA5RNyz7kqHVDNspjtXLuYiZZyp",
            },
        },
    );

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || `Request failed with status ${response.status}`);
    }

    return response.json();
}

async function safeSectionRequest(sectionId, queryParams = "", options = {}) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const url = new URL(root, window.location.origin);
    url.searchParams.set("sections", sectionId);

    if (queryParams && typeof queryParams === "object") {
        Object.entries(queryParams).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });
    }

    const response = await fetch(url.toString(), {
        credentials: "same-origin",
        ...options,
    });

    const text = await response.text().catch(() => "");
    if (!response.ok) {
        console.error("Section request failed", { sectionId, status: response.status, snippet: text.slice(0, 300), });
    }

    let parsed;
    try {
        parsed = JSON.parse(text);
    } catch (e) {
        console.error("Expected JSON from section rendering API but got non-JSON", { sectionId, status: response.status, snippet: text.slice(0, 300), });
    }

    return parsed;
}

async function safeFetchCart() {
    const sectionId = "cart-data";
    const sectionsJson = await safeSectionRequest(sectionId);
    if (!sectionsJson) {
        console.error("No section response received");
        return null;
    }

    const sectionHtml = sectionsJson[sectionId];
    if (!sectionHtml) {
        console.error("cart-data section not found in response", { sectionsJson });
        return null;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, "text/html");
    const scriptEl = doc.querySelector('script[type="application/json"]');

    if (!scriptEl) {
        console.error("Cart data JSON script not found in cart-data section", {sectionHtml});
        return null;
    }

    let cartPayload;
    try {
        cartPayload = JSON.parse(scriptEl.textContent.trim());
    } catch (e) {
        console.error("Failed to parse cart JSON from cart-data section", {error: e, scriptContentSnippet: scriptEl.textContent.slice(0, 200)});
        return null;
    }

    if (!cartPayload || typeof cartPayload.cart === "undefined") {
        console.error("cart property missing in cart-data payload", {cartPayload});
        return null;
    }

    return cartPayload.cart;
}

async function setCartAttributeWithFormData(formData) {
    const sectionsToFetch = 'side-cart';
    formData.append('sections', sectionsToFetch);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const response = await fetch(`${root}cart/update.js`, {
        method: "POST",
        body: formData,
    });
    updateCartPanel(response, false);
    if (!response.ok) {
        throw new Error(`Cart update failed with status ${response.status}`);
    }
    return response.json();
}

function getProductPickupLocations(prodInventory) {
    return (prodInventory?.locations || [])
        .filter(
            (location) =>
                location.isActive &&
                location.hasActiveInventory &&
                location.offersPickup &&
                Number(location.availableQuantity) > 0,
        )
        .map((location) => ({
            id: location.id,
            name: location.name,
            city: location.address?.city || "",
            provinceCode: location.address?.provinceCode || "",
            zip: location.address?.zip || "",
        }));
}

function getProductShippingLocations(prodInventory) {
    return (prodInventory?.locations || [])
        .filter(
            (location) =>
                location.isActive &&
                location.hasActiveInventory &&
                location.offersShipping &&
                Number(location.availableQuantity) > 0,
        )
        .map((location) => ({
            id: location.id,
            name: location.name,
            city: location.address?.city || "",
            provinceCode: location.address?.provinceCode || "",
            zip: location.address?.zip || "",
        }));
}

function setFulfillmentData(prodInventory, fulfillmentType) {
    if (!prodInventory || !fulfillmentType) {
        return;
    }

    const type = fulfillmentType.toLowerCase();
    const shippingLocations = getProductShippingLocations(prodInventory);
    const pickupLocations = getProductPickupLocations(prodInventory);
    const fulfillmentData = { type, shippingAvailable: !!prodInventory.shippingAvailable, pickupAvailable: !!prodInventory.pickupAvailable, shippingLocations, pickupLocations };

    window.selectedFulfillment = fulfillmentData;
    document.cookie = [`${FULFILLMENT_COOKIE}=${encodeURIComponent(JSON.stringify(fulfillmentData))}`, "path=/", `max-age=${FULFILLMENT_COOKIE_MAX_AGE}`, "SameSite=Lax"].join("; ");
}

function getFulfillmentData() {
    const cookie = document.cookie.split("; ").find((row) => row.startsWith(`${FULFILLMENT_COOKIE}=`));
    if (!cookie) {
        return null;
    }
    try {
        return JSON.parse(decodeURIComponent(cookie.substring(FULFILLMENT_COOKIE.length + 1)));
    } catch (error) {
        console.error("Invalid fulfillment cookie:", error);
        return null;
    }
}

function initializeFulfillmentData() {
    if (!window.selectedFulfillment) {
        window.selectedFulfillment = getFulfillmentData();
    }

    return window.selectedFulfillment;
}

function getCommonPickupLocations(prodInventory) {
    const fulfillmentData = window.selectedFulfillment || getFulfillmentData();
    if (!fulfillmentData || !Array.isArray(fulfillmentData.pickupLocations) || !fulfillmentData.pickupLocations.length) {
        return [];
    }

    const currentPickupLocations = getProductPickupLocations(prodInventory);
    const currentLocationIds = new Set(currentPickupLocations.map((location) => location.id));

    return fulfillmentData.pickupLocations.filter((location) =>
        currentLocationIds.has(location.id),
    );
}

function updateFulfillmentDataAfterAdd(prodInventory, fulfillmentType) {
    if (!prodInventory || !fulfillmentType) {
        return null;
    }

    const type = fulfillmentType.toLowerCase();
    const existingData = window.selectedFulfillment || getFulfillmentData();
    const currentShippingLocations = getProductShippingLocations(prodInventory);
    const currentPickupLocations = getProductPickupLocations(prodInventory);
    let shippingLocations = currentShippingLocations;
    let pickupLocations = currentPickupLocations;

    if (existingData?.type === type) {
        if (type === "pickup" && existingData?.type === "pickup" && Array.isArray(existingData.pickupLocations) && existingData.pickupLocations.length) {
            const currentPickupIds = new Set(currentPickupLocations.map((location) => location.id));
            pickupLocations = existingData.pickupLocations.filter((location) => currentPickupIds.has(location.id));
        }

        if (type === "shipping" && Array.isArray(existingData.shippingLocations) && existingData.shippingLocations.length) {
            const currentShippingIds = new Set(currentShippingLocations.map((location) => location.id));
            shippingLocations = existingData.shippingLocations.filter((location) => currentShippingIds.has(location.id));
        }
    }
    const fulfillmentData = { type, shippingAvailable: !!prodInventory.shippingAvailable, pickupAvailable: !!prodInventory.pickupAvailable, shippingLocations, pickupLocations };
    window.selectedFulfillment = fulfillmentData;
    document.cookie = [`${FULFILLMENT_COOKIE}=${encodeURIComponent(JSON.stringify(fulfillmentData))}`, "path=/", `max-age=${FULFILLMENT_COOKIE_MAX_AGE}`, "SameSite=Lax"].join("; ");
    return fulfillmentData;
}

function setCartItemFulfillmentType(cartType) {
    const productInfo = document.querySelector('.main-product-info');

    if (!productInfo) return;

    const forms = productInfo.querySelectorAll('form[action="/cart/add"], form[action$="/cart/add.js"]');

    forms.forEach((form) => {
        let input = form.querySelector('input[name="properties[_bm_cart_type]"]');

        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'properties[_bm_cart_type]';
            form.appendChild(input);
        }

        input.value = cartType;
    });
}

function cleanupFulfillmentListeners() {

    fulfillmentListeners.cardClickListeners.forEach(({ card, listener }) => {
        card.removeEventListener("click", listener);
    });
    fulfillmentListeners.cardClickListeners = [];

    fulfillmentListeners.cardKeydownListeners.forEach(({ card, listener }) => {
        card.removeEventListener("keydown", listener);
    });
    fulfillmentListeners.cardKeydownListeners = [];

    if (fulfillmentListeners.addToCartListener) {
        window.removeEventListener("product:addedToCart", fulfillmentListeners.addToCartListener);
        fulfillmentListeners.addToCartListener = null;
    }

    if (fulfillmentListeners.storeSelectListener) {
        document.removeEventListener("click", fulfillmentListeners.storeSelectListener);
        fulfillmentListeners.storeSelectListener = null;
    }
}

async function initializeFulfillmentUI() {

    const loaderRoot = document.querySelector("product-shipping-bopis .product-shipping-bopis__inner");
    loaderRoot?.classList.add("bopis_loading");

    cleanupFulfillmentListeners();

    let cartType = null;
    let setCartType = null;
    let prodInventory = null;
    let variantId = null;

    const container = document.querySelector(".product-shipping-bopis__inner");
    if (!container) {
        console.warn("Container not found");
        return;
    }

    const shippingCard = container.querySelector(".product-shipping-bopis__col.product-shipping-bopis__col--shipping");
    const pickupCard = container.querySelector(".product-shipping-bopis__col.product-shipping-bopis__col--pickup");
    const cards = [shippingCard, pickupCard].filter(Boolean);

    if (!cards.length) {
        console.warn("No fulfillment cards found");
        return;
    }

    initializeFulfillmentData();

    variantId = document.querySelector(".product-variant-id")?.value || null;
    if (!variantId) {
        console.warn("Variant ID not found.");
    }

    let cartResult;
    let inventoryResult;
    try {
        [cartResult, inventoryResult] = await Promise.allSettled([
            safeFetchCart(),
            variantId ? getVariantInventoryLocations(variantId) : Promise.resolve(null),
        ]);
    } finally {
        loaderRoot?.classList.remove("bopis_loading");
    }
    
    cartType = cartResult.value?.attributes?.bm_cart_type || null;

    if (inventoryResult.status === "fulfilled") {
        prodInventory = inventoryResult.value;
    } else {
        console.warn("Could not fetch inventory locations:", inventoryResult.reason);
    }

    const selectCard = (card) => {
        if (!card || card.classList.contains("product-shipping-bopis__col--disabled")) {
            return;
        }

        cards.forEach((c) => {
            c.classList.remove("product-shipping-bopis__col--selected");
            c.setAttribute("aria-checked", "false");
            c.setAttribute("tabindex", "-1");
        });

        card.classList.add("product-shipping-bopis__col--selected");
        card.setAttribute("aria-checked", "true");
        card.setAttribute("tabindex", "0");

        if (shippingCard?.classList.contains("product-shipping-bopis__col--selected")) {
            setCartType = "Shipping";
        } else if (pickupCard?.classList.contains("product-shipping-bopis__col--selected")) {
            setCartType = "PickUp";
        }
        setCartItemFulfillmentType(setCartType);
    };

    const firstAvailableCard = cards.find((card) => !card.classList.contains("product-shipping-bopis__col--disabled"));
    if (firstAvailableCard) {
        selectCard(firstAvailableCard);
    }

    cards.forEach((card) => {
        const clickListener = () => selectCard(card);
        card.addEventListener("click", clickListener);
        fulfillmentListeners.cardClickListeners.push({ card, listener: clickListener });

        const keydownListener = (e) => {
            if (card.classList.contains("product-shipping-bopis__col--disabled") && e.key !== "Tab") {
                return;
            }

            switch (e.key) {
                case "Enter":
                case " ":
                    e.preventDefault();
                    if (!card.classList.contains("product-shipping-bopis__col--disabled")) {
                        selectCard(card);
                    }
                    break;

                case "ArrowRight":
                case "ArrowDown": {
                    e.preventDefault();
                    const nextCard = cards[cards.indexOf(card) + 1];
                    if (nextCard && !nextCard.classList.contains("product-shipping-bopis__col--disabled")) {
                        nextCard.focus();
                        selectCard(nextCard);
                    }
                    break;
                }

                case "ArrowLeft":
                case "ArrowUp": {
                    e.preventDefault();
                    const prevCard = cards[cards.indexOf(card) - 1];
                    if (prevCard && !prevCard.classList.contains("product-shipping-bopis__col--disabled")) {
                        prevCard.focus();
                        selectCard(prevCard);
                    }
                    break;
                }

                case "Home":
                    e.preventDefault();
                    if (shippingCard && !shippingCard.classList.contains("product-shipping-bopis__col--disabled")) {
                        shippingCard.focus();
                        selectCard(shippingCard);
                    }
                    break;

                case "End": {
                    e.preventDefault();
                    const endCard = pickupCard && !pickupCard.classList.contains("product-shipping-bopis__col--disabled") ? pickupCard : shippingCard;
                    if (endCard && !endCard.classList.contains("product-shipping-bopis__col--disabled")) {
                        endCard.focus();
                        selectCard(endCard);
                    }
                    break;
                }
            }
        };
        card.addEventListener("keydown", keydownListener);
        fulfillmentListeners.cardKeydownListeners.push({ card, listener: keydownListener });
    });

    fulfillmentListeners.addToCartListener = async function (evt) {
        if (variantId == evt.detail.id) {
            try {
                if (!prodInventory) {
                    console.warn("Product inventory not available when product was added.");
                    return;
                }

                const fulfillmentType = cartType || setCartType;

                if (!fulfillmentType) {
                    console.warn("No fulfillment type selected.");
                    return;
                }

                const formData = new FormData();

                if (fulfillmentType === "Shipping") {
                    formData.append("attributes[bm_cart_type]", "Shipping");
                    formData.append("attributes[bm_store_location_address]", "");
                    formData.append("attributes[bm_store_location_zip]", "");

                    if (!window.selectedFulfillment || window.selectedFulfillment.type !== "shipping") {
                        setFulfillmentData(prodInventory, "shipping");
                    } else {
                        updateFulfillmentDataAfterAdd(prodInventory, "shipping");
                    }
                } else if (fulfillmentType === "PickUp") {
                    formData.append("attributes[bm_cart_type]", "PickUp");
                    const existingFulfillment = window.selectedFulfillment || getFulfillmentData();
                    let updatedFulfillment;
                    if (!existingFulfillment || existingFulfillment.type !== "pickup" || !existingFulfillment.pickupLocations?.length) {
                        updatedFulfillment = setFulfillmentData(prodInventory, "pickup");
                    } else {
                        updatedFulfillment = updateFulfillmentDataAfterAdd(prodInventory, "pickup");
                    }
                }

                if(!cartType){
                    await setCartAttributeWithFormData(formData);
                }
                cartType = fulfillmentType;
                setCartType = fulfillmentType;
            } catch (error) {
                console.error("Failed to set cart fulfillment data:", error);
            }
        }
        initializeFulfillmentUI();
    };

    window.addEventListener("product:addedToCart", fulfillmentListeners.addToCartListener);
}

document.addEventListener("initializeFulfillment", () => {
    initializeFulfillmentUI();
});

document.addEventListener("DOMContentLoaded", () => {
    initializeFulfillmentUI();
});